import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { IconType } from '@react-icons/all-files';
import { FaBan } from '@react-icons/all-files/fa/FaBan';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';
import { FaReply } from '@react-icons/all-files/fa/FaReply';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import { FaUndo } from '@react-icons/all-files/fa/FaUndo';

import { formatMessageTime } from '../../../../../../utils/dates';
import { formatMoney } from '../../../../../../utils/helpers';
import { MarketplaceOffer } from '../offers/utils';
import OfferNote from './OfferNote';

type MoneyCardProps = {
  offer: MarketplaceOffer;
};

type MoneyEventView = {
  role: string;
  icon: IconType;
  description: string;
  isWin: boolean;
  isExpired: boolean;
};

const ROLE_BY_TYPE: Partial<Record<MarketplaceOffer['type'], string>> = {
  dealer_offer: 'Buyer offer',
  dealer_counter: 'Buyer counter',
  driver_counter: 'Your counter',
};

const WITHDRAWN_DESCRIPTION: Partial<Record<MarketplaceOffer['type'], string>> = {
  dealer_offer: 'The buyer withdrew their offer',
  dealer_counter: 'The buyer withdrew their counter',
  driver_counter: 'You withdrew your counter',
};

// Maps a terminal money event to its one-line outcome; `accepted` is the only non-struck state.
const getMoneyEventView = (offer: MarketplaceOffer): MoneyEventView => {
  const role = ROLE_BY_TYPE[offer.type] ?? 'Offer';
  const isDriver = offer.type === 'driver_counter';

  switch (offer.status) {
    case 'accepted':
      return {
        role: 'Accepted offer',
        icon: FaCheck,
        description: isDriver ? 'The buyer accepted your counter' : 'You accepted this offer',
        isWin: true,
        isExpired: false,
      };
    case 'countered':
      return {
        role,
        icon: FaReply,
        description: isDriver ? 'The buyer countered back' : 'You countered this',
        isWin: false,
        isExpired: false,
      };
    case 'rejected':
      return {
        role,
        icon: FaTimesCircle,
        description: 'Declined by the buyer',
        isWin: false,
        isExpired: false,
      };
    case 'withdrawn':
      return {
        role,
        icon: FaUndo,
        description: WITHDRAWN_DESCRIPTION[offer.type] ?? 'The buyer withdrew their offer',
        isWin: false,
        isExpired: false,
      };
    case 'expired':
      return {
        role,
        icon: FaRegClock,
        description: isDriver ? 'This counter expired' : 'Expired before you responded',
        isWin: false,
        isExpired: true,
      };
    case 'edited':
      return {
        role,
        icon: FaReply,
        description: isDriver ? 'Replaced by an updated counter' : 'Replaced by an updated offer',
        isWin: false,
        isExpired: false,
      };
    case 'outbid':
    case 'superseded':
      return {
        role,
        icon: FaBan,
        description: 'Closed when you accepted another offer',
        isWin: false,
        isExpired: false,
      };
    default:
      return {
        role,
        icon: FaBan,
        description: 'This offer is no longer available',
        isWin: false,
        isExpired: false,
      };
  }
};

const MoneyCard = ({ offer }: MoneyCardProps) => {
  const isDriver = offer.type === 'driver_counter';
  const { role, icon, description, isWin, isExpired } = getMoneyEventView(offer);

  const stripBg = isExpired ? 'leaseEndRedBG' : isWin ? 'offerTopBadgeBG' : 'offerInactiveStripBG';
  const stripInk = isExpired ? 'leaseEndRed' : isWin ? 'greenDark' : 'grayLight';
  const stripIconColor = isExpired ? 'leaseEndRed' : isWin ? 'leaseEndGreen' : 'grayMedium';

  return (
    <Flex direction="column" align={isDriver ? 'flex-end' : 'flex-start'}>
      <Box
        position="relative"
        overflow="hidden"
        maxW="80%"
        borderWidth="1px"
        borderColor={isWin ? 'offerTopCardBorder' : 'gainsboro'}
        borderRadius="11px"
        bg="white"
        bgGradient={isWin ? 'linear(96deg, offerTopCardTint 0%, white 60%)' : undefined}
        px="15px"
        py="12px"
      >
        <Box
          position="absolute"
          insetStart={0}
          top={0}
          bottom={0}
          w="3px"
          bg={isWin ? 'leaseEndGreen' : 'offerInactiveAccent'}
        />

        <Text
          as="span"
          display="block"
          fontFamily="heading"
          fontSize="10.5px"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color={isWin ? 'greenDark' : 'grayMedium'}
          mb="2px"
        >
          {role}
        </Text>

        <Text
          as="span"
          display="block"
          fontSize="21px"
          fontWeight="700"
          lineHeight="1.15"
          color={isWin ? 'greenDark' : 'offerInactiveInk'}
          textDecoration={isWin ? undefined : 'line-through'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMoney(offer.amount)}
        </Text>

        <HStack
          spacing="8px"
          mt="9px"
          bg={stripBg}
          borderWidth="1px"
          borderColor={isExpired || isWin ? 'transparent' : 'gainsboro'}
          borderRadius="8px"
          px="11px"
          py="7px"
        >
          <Icon as={icon} boxSize="13px" color={stripIconColor} flexShrink={0} />
          <Text fontSize="12px" fontWeight="500" color={stripInk}>
            {description}
          </Text>
        </HStack>

        <OfferNote note={offer.notes} marginTop="8px" />
      </Box>
      <Text mt="3px" fontSize="10.5px" color="grayMedium" textAlign={isDriver ? 'right' : 'left'}>
        {formatMessageTime(offer.createdAt)}
      </Text>
    </Flex>
  );
};

export default MoneyCard;
